// Admin panel shell: navigation filtered by the signed-in person's permissions.
import { t, tl } from "../lib/i18n";
import { Link, match, navigate, route, startsWith } from "../lib/router";
import { can, canAny, session, signOut } from "../lib/session";
import { h, local } from "../lib/ui";
import { AccountPanel } from "../shared/auth";
import { LangSwitch, Wordmark } from "../shared/components";
import { Icon, type IconName } from "../shared/icons";
import { AuditPage } from "./audit";
import { BrandStudio } from "./brand";
import { ChecklistPage } from "./checklists";
import { HomeDetail, HomesPage } from "./homes";
import { Overview } from "./overview";
import { PartnersPage } from "./partners";
import { RolesPage } from "./roles";
import { SettingsPage } from "./settings";
import { EmailPage } from "./email";
import { UsersPage } from "./users";
import { TicketDetail, TicketsPage } from "./tickets";
import { VisitsPage } from "./visits";

interface NavItem {
  href: string;
  label: string;
  icon: IconName;
  perms: string[];
}

function navGroups(): { label: string; items: NavItem[] }[] {
  return [
    {
      label: t("admin.nav.operations"),
      items: [
        { href: "/admin", label: t("admin.nav.overview"), icon: "overview", perms: ["ops.homes", "ops.tickets", "ops.visits", "admin.brand", "admin.settings", "admin.staff"] },
        { href: "/admin/homes", label: t("admin.nav.homes"), icon: "home", perms: ["ops.homes"] },
        { href: "/admin/tickets", label: t("admin.nav.tickets"), icon: "tool", perms: ["ops.tickets"] },
        { href: "/admin/visits", label: t("admin.nav.visits"), icon: "calendar", perms: ["ops.visits"] },
        { href: "/admin/partners", label: t("admin.nav.partners"), icon: "briefcase", perms: ["ops.partners"] },
        { href: "/admin/checklists", label: t("admin.nav.checklists"), icon: "checklist", perms: ["ops.checklists"] },
      ],
    },
    {
      label: t("admin.nav.administration"),
      items: [
        { href: "/admin/brand", label: t("admin.nav.brand"), icon: "palette", perms: ["admin.brand"] },
        { href: "/admin/settings", label: t("admin.nav.settings"), icon: "sliders", perms: ["admin.settings"] },
        { href: "/admin/roles", label: t("admin.nav.roles"), icon: "shield", perms: ["admin.roles"] },
        { href: "/admin/users", label: t("admin.nav.users"), icon: "users", perms: ["admin.staff", "ops.members", "admin.users"] },
        { href: "/admin/email", label: t("admin.nav.email"), icon: "mail", perms: ["admin.settings"] },
        { href: "/admin/audit", label: t("admin.nav.audit"), icon: "log", perms: ["admin.audit"] },
      ],
    },
  ];
}

function isCurrent(href: string) {
  return href === "/admin" ? route.path === "/admin" : startsWith(href);
}

function Denied() {
  return <div class="panel panel-pad"><p>{t("admin.denied")}</p></div>;
}

function Page() {
  const p = route.path;
  const guard = (perms: string[], view: () => any) => (canAny(...perms) ? view() : <Denied />);
  let m;
  if (p === "/admin") return guard(navGroups()[0].items[0].perms, () => <Overview />);
  if (p === "/admin/homes") return guard(["ops.homes"], () => <HomesPage />);
  if ((m = match("/admin/homes/:id"))) return guard(["ops.homes"], () => <HomeDetail id={m.id} />);
  if (p === "/admin/tickets") return guard(["ops.tickets"], () => <TicketsPage />);
  if ((m = match("/admin/tickets/:id"))) return guard(["ops.tickets"], () => <TicketDetail id={m.id} />);
  if (p === "/admin/visits") return guard(["ops.visits"], () => <VisitsPage />);
  if (p === "/admin/partners") return guard(["ops.partners"], () => <PartnersPage />);
  if (p === "/admin/checklists") return guard(["ops.checklists"], () => <ChecklistPage />);
  if (p === "/admin/brand") return guard(["admin.brand"], () => <BrandStudio />);
  if (p === "/admin/settings") return guard(["admin.settings"], () => <SettingsPage />);
  if (p === "/admin/roles") return guard(["admin.roles"], () => <RolesPage />);
  if (p === "/admin/users") return guard(["admin.staff", "ops.members", "admin.users"], () => <UsersPage />);
  if (p === "/admin/staff") {
    queueMicrotask(() => navigate("/admin/users", { replace: true }));
    return null;
  }
  if (p === "/admin/email") return guard(["admin.settings"], () => <EmailPage />);
  if (p === "/admin/audit") return guard(["admin.audit"], () => <AuditPage />);
  if (p === "/admin/account")
    return (
      <div class="stack loose">
        <div class="page-head"><h1>{t("account.title")}</h1></div>
        <AccountPanel />
      </div>
    );
  return <div class="panel panel-pad">{t("common.notFound")}</div>;
}

export function AdminApp() {
  const ui = local("admin.shell", () => ({ navOpen: false }));
  const me = session.me!;
  return (
    <div class={`admin ${ui.navOpen ? "nav-open" : ""}`}>
      <nav class="admin-nav" aria-label={t("admin.nav.label")} onClick={(e: MouseEvent) => (e.target as Element).closest("a") && ui.set({ navOpen: false })}>
        <Wordmark href="/admin" />
        {navGroups().map((g) => {
          const items = g.items.filter((i) => canAny(...i.perms));
          if (!items.length) return null;
          return (
            <div class="group">
              <div class="group-label">{g.label}</div>
              {items.map((i) => (
                <Link href={i.href} aria-current={isCurrent(i.href) ? "page" : undefined}>
                  <Icon name={i.icon} />
                  <span>{i.label}</span>
                </Link>
              ))}
            </div>
          );
        })}
        {can("field.visits") ? (
          <div class="group">
            <Link href="/field"><Icon name="route" /><span>{t("admin.nav.fieldApp")}</span></Link>
          </div>
        ) : null}
        <div class="me">
          <div>
            <div class="strong">{me.name}</div>
            <div class="xs" style="opacity:.7">{tl(me.staffRole?.name)}</div>
          </div>
          <div class="row between">
            <LangSwitch />
            <div class="row">
              <Link href="/admin/account" title={t("account.title")} class="icon-btn"><Icon name="user" size={18} /></Link>
              <button class="icon-btn" title={t("common.signOut")} aria-label={t("common.signOut")} onClick={async () => { await signOut(); navigate("/login"); }}>
                <Icon name="logout" size={18} />
              </button>
            </div>
          </div>
        </div>
      </nav>
      <div class="admin-main">
        <div class="admin-top">
          <button class="icon-btn admin-burger" aria-label={t("admin.nav.open")} onClick={() => ui.set({ navOpen: !ui.navOpen })}>
            <Icon name="menu" />
          </button>
        </div>
        <main class="admin-page">
          <Page />
        </main>
      </div>
    </div>
  );
}

/** Page heading with optional breadcrumb and actions. */
export function PageHead(props: { title: string; intro?: string; crumbs?: { href: string; label: string }[]; actions?: any }) {
  return (
    <div class="stack tight">
      {props.crumbs ? (
        <nav class="crumbs small muted row" aria-label={t("common.breadcrumb")} style="gap:6px">
          {props.crumbs.map((c) => [<Link href={c.href}>{c.label}</Link>, <span aria-hidden="true">/</span>])}
        </nav>
      ) : null}
      <div class="page-head">
        <div>
          <h1>{props.title}</h1>
          {props.intro ? <p>{props.intro}</p> : null}
        </div>
        {props.actions ? <div class="row">{props.actions}</div> : null}
      </div>
    </div>
  );
}
